import { supabase } from "./supabase";

export type TeamInviteRow = {
  id: string;
  team_id: string;
  slug: string;
  created_by: string;
  expires_at: string | null;
  max_uses: number | null;
  uses_count: number;
  revoked_at: string | null;
  created_at: string;
};

export type TeamInvitePreview = {
  team_id: string | null;
  team_name: string | null;
  competition_id: string | null;
  competition_name: string | null;
  captain_name: string | null;
  member_count: number | null;
  team_size_max: number | null;
  expires_at: string | null;
  is_valid: boolean;
  invalid_reason: string | null;
};

export type AcceptResult =
  | { ok: true; teamId: string; competitionId: string; message: string | null }
  | { ok: false; error: string };

function randomSlug(len = 14): string {
  // URL-safe alphabet — no easily-confused chars (0/O, 1/I/l).
  const alphabet = "abcdefghjkmnpqrstuvwxyz23456789";
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = new Uint8Array(len);
    crypto.getRandomValues(bytes);
    let out = "";
    for (const b of bytes) out += alphabet[b % alphabet.length];
    return out;
  }
  let out = "";
  for (let i = 0; i < len; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

export async function listTeamInvites(teamId: string): Promise<TeamInviteRow[]> {
  const { data, error } = await supabase
    .from("team_invites")
    .select("*")
    .eq("team_id", teamId)
    .order("created_at", { ascending: false });
  if (error || !data) return [];
  return data as TeamInviteRow[];
}

/**
 * Creates a new invite. Generates a slug client-side; if the random slug
 * happens to collide (vanishingly unlikely with 14 chars × 31 alphabet), the
 * unique-constraint error bubbles back as Error and the caller can retry.
 */
export async function createTeamInvite(params: {
  teamId: string;
  expiresAt?: Date | null;
  maxUses?: number | null;
}): Promise<{ invite: TeamInviteRow | null; error: Error | null }> {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userData.user) {
    return { invite: null, error: new Error(userErr?.message ?? "Not signed in") };
  }
  const slug = randomSlug();
  const { data, error } = await supabase
    .from("team_invites")
    .insert({
      team_id: params.teamId,
      slug,
      created_by: userData.user.id,
      expires_at: params.expiresAt ? params.expiresAt.toISOString() : null,
      max_uses: params.maxUses ?? null,
    })
    .select("*")
    .single();
  if (error || !data) return { invite: null, error: new Error(error?.message ?? "Failed to create invite") };
  return { invite: data as TeamInviteRow, error: null };
}

export async function revokeTeamInvite(inviteId: string): Promise<{ error: Error | null }> {
  const { error } = await supabase
    .from("team_invites")
    .update({ revoked_at: new Date().toISOString() })
    .eq("id", inviteId);
  return { error: error ? new Error(error.message) : null };
}

export async function fetchInvitePreview(slug: string): Promise<TeamInvitePreview | null> {
  const { data, error } = await supabase.rpc("team_invite_preview", { p_slug: slug });
  if (error || !data || !Array.isArray(data) || data.length === 0) {
    if (error) {
      return {
        team_id: null,
        team_name: null,
        competition_id: null,
        competition_name: null,
        captain_name: null,
        member_count: null,
        team_size_max: null,
        expires_at: null,
        is_valid: false,
        invalid_reason: error.message,
      };
    }
    return null;
  }
  const r = data[0] as Partial<TeamInvitePreview>;
  return {
    team_id: r.team_id ?? null,
    team_name: r.team_name ?? null,
    competition_id: r.competition_id ?? null,
    competition_name: r.competition_name ?? null,
    captain_name: r.captain_name ?? null,
    member_count: r.member_count ?? null,
    team_size_max: r.team_size_max ?? null,
    expires_at: r.expires_at ?? null,
    is_valid: Boolean(r.is_valid),
    invalid_reason: r.invalid_reason ?? null,
  };
}

export async function acceptInvite(slug: string): Promise<AcceptResult> {
  const { data, error } = await supabase.rpc("team_invite_accept", { p_slug: slug });
  if (error) return { ok: false, error: error.message };
  if (!Array.isArray(data) || data.length === 0) {
    return { ok: false, error: "Empty response from server." };
  }
  const r = data[0] as { status: string; team_id: string | null; competition_id: string | null; message: string | null };
  if (r.status === "ok" && r.team_id && r.competition_id) {
    return { ok: true, teamId: r.team_id, competitionId: r.competition_id, message: r.message };
  }
  return { ok: false, error: r.message ?? "Could not accept invite." };
}

/**
 * Build the public URL for an invite slug. On web we use the current origin;
 * on native we fall back to the production marketing domain. Either way the
 * URL resolves to /teams/invite/<slug> in the Expo Router web build.
 */
export function buildInviteUrl(slug: string): string {
  if (typeof window !== "undefined" && window.location) {
    return `${window.location.origin}/teams/invite/${slug}`;
  }
  return `https://coravu.com/teams/invite/${slug}`;
}
